import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { OptionReminder } from '@/components/ui/option-reminder';
import { Separator } from '@/components/ui/separator';
import {
  DatasourceInstructionMode,
  SaleProductPreview,
} from '@/lib/graphql/generated/graphql';
import { storeAtom } from '@/states/store.state';
import { CurrencyCode } from '@/types/currency';
import { Locale } from '@/types/locale';
import { Store } from '@/types/store';
import { useAtomValue } from 'jotai';
import { ArrowLeft, CheckCircle, Package } from 'lucide-react';

interface SaleProductsPreviewProps {
  products: SaleProductPreview[];
  loading: boolean;
  error?: Error | null;
  onConfirm: () => void;
  onBack: () => void;
  saleName: string;
  confirming?: boolean;
  instructionMode?: DatasourceInstructionMode;
  onModifyOption?: () => void;
}

export function SaleProductsPreview({
  products,
  loading,
  error,
  onConfirm,
  onBack,
  saleName,
  confirming = false,
  instructionMode,
  onModifyOption,
}: SaleProductsPreviewProps) {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  const formatPrice = (priceInCents: number) => {
    return new Intl.NumberFormat(store === Store.FR ? Locale.FR : Locale.US, {
      style: 'currency',
      currency: store === Store.FR ? CurrencyCode.EUR : CurrencyCode.USD,
    }).format(priceInCents / 100);
  };

  const getOptimizedImageUrl = (url: string | null | undefined): string => {
    if (!url) return '';
    // Replace [W] and [H] placeholders with 200x200 for thumbnails
    return url.replace(/\[W\]/g, '200').replace(/\[H\]/g, '200');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex flex-col items-center gap-2">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-xs font-medium text-gray-700">
            {t(
              'components.import_flow.copy_previous_sales_preview.loading_products'
            )}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center">
        <div className="text-sm text-red-500">
          {t(
            'components.import_flow.copy_previous_sales_preview.error_loading'
          )}
        </div>
        <Button variant="outline" size="sm" onClick={onBack} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('general.previous')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {t(
              'components.import_flow.copy_previous_sales_preview.preview_title'
            )}
          </h3>
          <p className="text-sm text-gray-600">
            {t(
              'components.import_flow.copy_previous_sales_preview.selected_sale'
            )}{' '}
            : <span className="font-medium">{saleName}</span>
          </p>
        </div>
        <Badge variant="secondary" className="text-xs">
          {t('general.product_count', { count: products.length })}
        </Badge>
      </div>

      {instructionMode && onModifyOption && (
        <OptionReminder
          instructionMode={instructionMode}
          onModify={onModifyOption}
        />
      )}

      <Separator />

      {/* Products List */}
      <div className="max-h-[33vh] overflow-y-auto h-700:max-h-[330px]">
        {products.length === 0 ? (
          <div className="py-8 text-center">
            <Package className="mx-auto mb-3 h-10 w-10 text-gray-400" />
            <p className="text-sm text-gray-500">
              {t(
                'components.import_flow.copy_previous_sales_preview.no_products_found'
              )}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {products.map((product, index) => (
              <div
                key={`${product.saleId}-${index}`}
                className="mx-4 flex items-start gap-3 rounded-lg border p-4 transition-colors hover:bg-gray-50"
              >
                {/* Product Image */}
                <div className="flex-shrink-0">
                  {product.image && getOptimizedImageUrl(product.image) ? (
                    <img
                      src={getOptimizedImageUrl(product.image)}
                      alt={product.name}
                      width={48}
                      height={48}
                      className="h-12 w-12 rounded-md border object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-md bg-gray-200 ${
                      product.image ? 'hidden' : ''
                    }`}
                  >
                    <Package className="h-6 w-6 text-gray-400" />
                  </div>
                </div>

                {/* Product Info */}
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-medium leading-tight text-gray-900">
                    {product.name}
                  </h4>
                  <div className="mt-2 flex items-center gap-4">
                    <span className="text-sm font-semibold text-green-600">
                      {formatPrice(product.price)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {t('kpi.stock')}: {product.stock}
                    </span>
                  </div>
                </div>

                {/* Checkmark */}
                <div className="flex-shrink-0 pt-0.5">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <Separator />
      <div className="flex items-center justify-end gap-4 pt-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onBack}
          disabled={confirming}
        >
          {t('general.previous')}
        </Button>

        <Button
          onClick={onConfirm}
          disabled={products.length === 0 || confirming}
          size="sm"
        >
          {confirming ? (
            <>
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              {t('general.importation_in_progress')}
            </>
          ) : (
            <>{t('general.importation_confirm')}</>
          )}
        </Button>
      </div>
    </div>
  );
}
